function parseListFilter(raw) {
  if (raw == null || raw === "") return {};
  const text = String(raw).trim().replace(/'/g, '"');
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return {};
  }
  const objects = Array.isArray(parsed) ? parsed : [parsed];
  const out = {};
  for (const obj of objects) {
    if (obj && typeof obj === "object" && !Array.isArray(obj)) {
      Object.assign(out, obj);
    }
  }
  return out;
}

function positiveId(value) {
  if (Array.isArray(value)) {
    if (value.length !== 1) return null;
    return positiveId(value[0]);
  }
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

export function campaignIdFrom(variables = {}) {
  const direct = positiveId(variables.campaignId ?? variables.campaign_id);
  if (direct) return direct;
  const filter = parseListFilter(variables.filter);
  return positiveId(filter.campaign_id ?? filter["campaigns.campaign_id"]);
}

export function channelIdFrom(variables = {}) {
  const direct = positiveId(variables.channelId ?? variables.channel_id);
  if (direct) return direct;
  const filter = parseListFilter(variables.filter);
  return positiveId(filter.channel_id ?? filter["channels.channel_id"]);
}

export function placementIdFrom(variables = {}) {
  const direct = positiveId(variables.placementId ?? variables.placement_id);
  if (direct) return direct;
  const filter = parseListFilter(variables.filter);
  return positiveId(filter.placement_id);
}

export function siteIdFrom(variables = {}) {
  const direct = positiveId(variables.siteId ?? variables.site_id);
  if (direct) return direct;
  const sources = [variables.filter, variables.sitesFilter2, variables.filterSite];
  for (const source of sources) {
    const filter = parseListFilter(source);
    const id = positiveId(
      filter.site_id ?? filter["sites.site_id"] ?? filter["formats.site_id"]
    );
    if (id) return id;
  }
  return null;
}

function unixSeconds(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
}

export function readDateRange(variables = {}) {
  const pairs = [
    ["startDatetime", "endDatetime"],
    ["locationsStartDatetime2", "locationsEndDatetime2"],
  ];
  for (const [startKey, endKey] of pairs) {
    const start = unixSeconds(variables[startKey]);
    const end = unixSeconds(variables[endKey]);
    if (start && end) {
      const timezone = variables.timezone;
      return {
        start_datetime: start,
        end_datetime: end,
        ...(timezone ? { timezone: String(timezone) } : {}),
      };
    }
  }
  return null;
}

function blankName(value) {
  return value == null ? "" : String(value);
}

export function campaignListInput(variables = {}) {
  const filter = parseListFilter(variables.filter);
  const requested = Number(variables.limit);
  const limit = Number.isFinite(requested) && requested > 0 ? Math.min(requested, 500) : 50;
  const offset = Math.max(0, Number(variables.offset) || 0);
  const input = { limit, offset };

  if (Array.isArray(filter.campaign_status_id) && filter.campaign_status_id.length) {
    input.status_ids = filter.campaign_status_id.map((id) => Number(id)).filter((id) => Number.isInteger(id));
  }

  const siteId = positiveId(filter.site_id);
  if (siteId) input.site_id = siteId;

  const campaignId = positiveId(filter.campaign_id ?? filter["campaigns.campaign_id"]);
  const text =
    filter.campaign_name ||
    filter["advertisers.account_name"] ||
    filter["accounts.account_name"] ||
    null;
  if (campaignId) input.campaign_ids = [campaignId];
  else if (text) input.search = String(text);

  return input;
}

export function shapeCampaignRow(row) {
  return {
    ...row,
    campaign_description: row.campaign_description ?? null,
    account: {
      account_id: row.account?.account_id ?? row.account_id ?? null,
      account_name: blankName(row.account?.account_name),
    },
    advertiser: {
      account_id: row.advertiser?.account_id ?? row.advertiser_id ?? null,
      account_name: blankName(row.advertiser?.account_name),
    },
    team: {
      team_id: row.team?.team_id ?? row.team_id ?? null,
      team_name: blankName(row.team?.team_name),
    },
    campaign_status: {
      campaign_status_id: row.campaign_status?.campaign_status_id ?? row.campaign_status_id ?? null,
      status_descr: blankName(row.campaign_status?.status_descr),
    },
  };
}

export function browserGroupBy(group) {
  const text = String(group || "");
  if (text.includes("browser")) return "browser_name";
  if (text.includes("os_name")) return "os_name";
  return "os_type";
}

export function shapeBrowsers(rows, groupBy) {
  const idKey = groupBy === "browser_name" ? "browser_name" : groupBy === "os_name" ? "os_name" : "os_type";
  return {
    browsers: (rows || []).map((row) => ({
      _id: { [idKey]: row.key ?? "" },
      clicks: row.clicks ?? 0,
      impressions: row.impressions ?? 0,
      views: row.views ?? 0,
    })),
  };
}

export function shapeLocations(rows) {
  const locations = (rows || []).map((row) => ({
    _id: {
      country: row.country ?? "",
      region: row.region ?? "",
    },
    clicks: row.clicks ?? 0,
    views: row.views ?? 0,
    impressions: row.impressions ?? 0,
  }));
  const total_data = locations.reduce(
    (acc, row) => {
      acc.impressions += row.impressions;
      acc.views += row.views;
      acc.clicks += row.clicks;
      return acc;
    },
    { impressions: 0, views: 0, clicks: 0 }
  );
  return { locations, total_data };
}

export function wantsBrazil(variables = {}) {
  const match = `${variables.match || ""} ${variables.locationsMatch2 || ""}`;
  return /BR/.test(match);
}
